import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box, Card, CardContent, CardHeader, Container, Grid, Skeleton, IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ZicassoChat,
  BackNavigationAppBar,
  getFormattedDateLongMonthDDYYYY,
  OutlinedIcons,
  getBudgetPerPersonString,
  getTotalBudgetString,
} from '@marageti/z4-lib';
import {
  Message,
  Conversation,
  GetParticipantsResponse,
  TripRequest,
} from '@marageti/z4-sdk/lib/travel';
import { PersonType } from '@marageti/z4-sdk/lib/people';
import MobileAlertDialog from '../../../../components/legacy-trip-request-dialog';

const formatDepartAndReturnDates = (dates: string[]) => (
  `${getFormattedDateLongMonthDDYYYY(dates[0]) || ''} - ${getFormattedDateLongMonthDDYYYY(dates[1]) || ''}`
);

type Props = {
  conversationTitle?: string,
  conversation: Conversation | undefined,
  handleViewClick: (message: Message) => void,
  isError: boolean,
  isLoading: boolean,
  isValidating: boolean,
  messages: Message[] | undefined,
  lastMessageSeenId: string | undefined,
  participants: GetParticipantsResponse | undefined,
  sendMessage: (message: string) => void,
  loadMoreMessages: () => void,
  hasMoreMessages: boolean,
  viewer: {
    id: string | undefined,
    type: PersonType,
  },
  tripRequest: TripRequest | undefined,
  isLegacyTR?: boolean,
  readOnly?: boolean,
};

const MessagesView = ({
  conversationTitle = '',
  conversation,
  handleViewClick,
  isError,
  isLoading,
  isValidating,
  messages,
  lastMessageSeenId,
  participants,
  sendMessage,
  loadMoreMessages,
  hasMoreMessages,
  viewer,
  tripRequest,
  isLegacyTR = false,
  readOnly = false,
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const {
    OutlinedInformationCircleIcon,
  } = OutlinedIcons;

  if (isMobile) {
    return (
      <Container
        sx={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'primary.contrastText',
          zIndex: 1200,
          padding: 0,
        }}
      >
        {isLegacyTR && <MobileAlertDialog open={dialogOpen} onClose={handleDialogClose} />}
        <BackNavigationAppBar
          avatar={{
            size: 24,
            children: conversationTitle?.charAt(0),
          }}
          primary={conversationTitle || <Skeleton width={150} />}
          defaultPath={`/trip-requests/${tripRequest?.id}`}
          options={{
            replace: true,
          }}
          endAdornment={isLegacyTR ? (
            <IconButton onClick={handleDialogOpen}>
              <OutlinedInformationCircleIcon color="primary" />
            </IconButton>
          ) : null}
        />

        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            flex: 1,
            p: 0,
            minHeight: 'calc(100vh - 72px)',
          }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: '100%',
            height: 'calc(100dvh - 72px)',
            px: 3,
          }}
          >
            <ZicassoChat
              isError={isError}
              isLoading={isLoading}
              isValidating={isValidating}
              conversation={conversation}
              messages={messages}
              lastMessageSeenId={lastMessageSeenId}
              loadPreviousPages={hasMoreMessages}
              onLoadPreviousPage={loadMoreMessages}
              onSend={sendMessage}
              onViewClick={handleViewClick}
              participants={participants}
              viewer={viewer}
              readOnly={readOnly}
              messagingAlert={(
                <Alert
                  severity="info"
                  sx={{
                    borderColor: 'divider', px: 1.5, py: 1,
                  }}
                  variant="outlined"
                >
                  Always communicate through this messaging platform to ensure you’re covered by our terms of service, privacy policy and other safeguards.
                </Alert>
              )}
            />
          </Box>
        </Container>
      </Container>
    );
  }
  return (
    <Container
      maxWidth="xl"
      sx={{
        display: 'flex',
        flex: 1,
        pt: 3.75,
      }}
    >
      <Grid
        container
        spacing={1}
      >
        <Grid
          item
          desktop={4}
          sx={{
            display: 'block',
            borderRight: '1px solid #C2C2C2',
          }}
        >
          {/* Sticky Sidebar */}
          <Box
            sx={{
              position: 'sticky',
              top: () => {
                const toolbarHeight = theme.mixins.toolbar.minHeight;
                return `calc(${toolbarHeight}px + ${theme.spacing(6)})`;
              },
              pr: 3,
              pb: 3,
              height: 'calc(100vh - 186px)',
              overflow: 'auto',
            }}
          >
            <Stack spacing={4}>
              {isLegacyTR
                && (
                  <Alert
                    severity="warning"
                    sx={{
                      p: 3, backgroundColor: 'warning.light',
                    }}
                    variant="outlined"
                  >
                    <AlertTitle>Prior Communication</AlertTitle>
                    Zicasso has moved to a new messaging platform. Previous email communications are not included in this chat.
                  </Alert>
                )}
              <Box>
                <Typography variant="h4" sx={{ mb: 1.5 }}>Your Team</Typography>
                <Card>
                  <CardHeader
                    avatar={(
                      <Avatar sx={{
                        width: '40px', backgroundColor: 'common.gold', color: 'background.default', fontSize: '13px', fontWeight: 700,
                      }}
                      >
                        Z
                      </Avatar>
                    )}
                    title="Zicasso Care Specialist"
                    subheader="Specialist"
                    titleTypographyProps={{
                      variant: 'body',
                      fontWeight: 'bold',
                    }}
                  />
                </Card>
              </Box>
              <Box>
                {tripRequest && !tripRequest.visibilityFlags.hideDetails && (
                <>
                  <Typography variant="h4" sx={{ mb: 1.5 }}>Original Request</Typography>
                  <Card>
                    <CardContent>
                      <Stack spacing={0.25}>
                        <Typography variant="caption">{formatDepartAndReturnDates([tripRequest.departureDate, tripRequest.returnDate])}</Typography>
                        <Typography variant="caption">
                          {`Budget Per Person: ${getBudgetPerPersonString(tripRequest.totalBudget, tripRequest.numberOfAdults, tripRequest.numberOfChildren)}`}
                        </Typography>
                        <Typography variant="caption">{`Total Budget: ${getTotalBudgetString(tripRequest.totalBudget)}`}</Typography>
                        <Typography variant="caption">
                          {`${tripRequest.numberOfAdults} Adults${tripRequest.numberOfChildren ? `, ${tripRequest.numberOfChildren} Children` : ''}`}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </>
                )}
              </Box>
            </Stack>
          </Box>
        </Grid>
        <Grid
          item
          desktop={8}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: '100%',
            height: 'calc(100vh - 198px)',
          }}
          >
            <ZicassoChat
              isError={isError}
              isLoading={isLoading}
              isValidating={isValidating}
              conversation={conversation}
              messages={messages}
              lastMessageSeenId={lastMessageSeenId}
              loadPreviousPages={hasMoreMessages}
              onLoadPreviousPage={loadMoreMessages}
              onSend={sendMessage}
              onViewClick={handleViewClick}
              participants={participants}
              viewer={viewer}
              readOnly={readOnly}
              messagingAlert={(
                <Alert
                  severity="info"
                  sx={{
                    borderColor: 'divider', px: 1.5, py: 1, mb: 1.5,
                  }}
                  variant="outlined"
                >
                  Always communicate through this messaging platform to ensure you’re covered by our terms of service, privacy policy and other safeguards.
                </Alert>
                )}
            />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default MessagesView;
